import { useEffect, useState, useCallback } from 'react'
import PageHeader from '../../Components/PageHeader/PageHeader'
import { IndianRupee, TrendingUp, TrendingDown, BarChart3, CalendarRange } from 'lucide-react'
import { ApiHit } from '../../Api/ApiHit'
import { dailyStatsCashRecord } from '../../Constant/Constant'
import { getCahbookUser, getSelectedAccount } from '../../Storage/Storage'
import { dashboardOptions } from '../../Constant/Options'
import DateSearch from '../../Components/DateRangeFilter/DateSearch'
import CashFlowChart from '../../Components/CashFlowChart/CashFlowChart'

function Dashboard() {
  const [statsData, setStatsData] = useState({
    totalRecords: 0,
    totalIn: 0,
    totalOut: 0,
    netBalance: 0,
  })
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('')
  const [dateRangeMode, setDateRangeMode] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const convertDateToTimestamp = (dateString, isStartOfDay = true) => {
    const [year, month, day] = dateString.split('-').map(Number);
    if (isStartOfDay) {
      return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
    } else {
      return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
    }
  };

  const getDateRange = useCallback((period) => {
    if (!period || period === '') {
      return null;
    }

    if (period === 'daterange') {
      if (startDate && endDate) {
        return {
          start: convertDateToTimestamp(startDate, true),
          end: convertDateToTimestamp(endDate, false)
        };
      }
      return null;
    }

    const now = new Date();
    let startTimestamp = new Date();
    let endTimestamp = new Date();

    switch (period) {
      case 'today':
        startTimestamp.setHours(0, 0, 0, 0);
        endTimestamp.setHours(23, 59, 59, 999);
        break;
      case 'currentWeek':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        startTimestamp = startOfWeek;
        endTimestamp = new Date();
        break;
      case 'currentMonth':
        startTimestamp = new Date(now.getFullYear(), now.getMonth(), 1);
        endTimestamp = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last7Days':
        startTimestamp = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        endTimestamp = new Date();
        break;
      case 'last30Days':
        startTimestamp = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endTimestamp = new Date();
        break;
      default:
        return null;
    }

    return {
      start: startTimestamp.getTime(),
      end: endTimestamp.getTime()
    };
  }, [startDate, endDate]);

  useEffect(() => {
    fetchDashboardData()
  }, [selectedPeriod, startDate, endDate])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const user = getCahbookUser()
      const account = getSelectedAccount()
      const dateRange = getDateRange(selectedPeriod)

      const payload = {
        search: {
          accountId: account?._id,
          ...(!['SuperAdmin', 'Admin'].includes(user?.roleId?.roleName) && { userId: user?._id }),
          ...(dateRange && {
            date: {
              $gte: dateRange.start,
              $lte: dateRange.end
            }
          })
        },
      }

      const res = await ApiHit(payload, dailyStatsCashRecord)
      console.log('Dashboard daily stats:', res)

      if (res?.data?.summary) {
        setStatsData({
          totalRecords: res.data.summary.totalRecords || 0,
          totalIn: res.data.summary.totalCashIn || 0,
          totalOut: res.data.summary.totalCashOut || 0,
          netBalance: res.data.summary.overallNetBalance || 0,
        })
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePeriodChange = (e) => {
    const value = e.target.value;
    setSelectedPeriod(value);
    if (value === 'daterange') {
      setDateRangeMode(true);
      setStartDate('');
      setEndDate('');
    } else {
      setDateRangeMode(false);
      setStartDate('');
      setEndDate('');
    }
  };

  const handleDateRangeSearch = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleClearSearch = () => {
    setStartDate('');
    setEndDate('');
    setSelectedPeriod('');
    setDateRangeMode(false);
  };

  const { totalRecords, totalIn, totalOut, netBalance } = statsData

  const stats = [
    {
      id: 1,
      title: 'Total Records',
      value: loading ? 'Loading...' : totalRecords.toLocaleString('en-IN'),
      icon: <BarChart3 className="w-6 h-6 text-blue-500" />,
      color: 'bg-blue-200',
    },
    {
      id: 2,
      title: 'Total Cash In',
      value: loading ? 'Loading...' : `₹${totalIn.toLocaleString('en-IN')}`,
      icon: <TrendingUp className="w-6 h-6 text-green-600" />,
      color: 'bg-green-200',
    },
    {
      id: 3,
      title: 'Total Cash Out',
      value: loading ? 'Loading...' : `₹${totalOut.toLocaleString('en-IN')}`,
      icon: <TrendingDown className="w-6 h-6 text-red-600" />,
      color: 'bg-red-200',
    },
    {
      id: 4,
      title: 'Net Balance',
      value: loading ? 'Loading...' : `₹${netBalance.toLocaleString('en-IN')}`,
      icon: <IndianRupee className="w-6 h-6 text-purple-600" />,
      color: 'bg-purple-200',
    },
  ]

  return (
    <div>
      {/* Page Header */}
      <div className='flex md:flex-row flex-col  md:items-center md:justify-between'>
        <PageHeader title="Dashboard" subtitle="Overview of cashbook performance" />
        <div className='flex flex-col sm:flex-row gap-2 items-stretch sm:items-center mb-4'>
          <select
            className="h-10 sm:h-9 text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={selectedPeriod}
            onChange={handlePeriodChange}
          >
            {dashboardOptions.map((option, index) => (
              <option key={index} value={option.value}>{option.label}</option>
            ))}
          </select>
          {loading && <span className="text-xs sm:text-sm text-blue-600 font-medium">Loading...</span>}
        </div>

      </div>
      {/* Period Filter Controls */}
      <div className="mt-4 sm:mt-6">
        {/* <div className='flex flex-col sm:flex-row gap-2 items-stretch sm:items-center mb-4'>
          <select
            className="h-10 sm:h-9 text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={selectedPeriod}
            onChange={handlePeriodChange}
          >
            {dashboardOptions.map((option, index) => (
              <option key={index} value={option.value}>{option.label}</option>
            ))}
          </select>
          {loading && <span className="text-xs sm:text-sm text-blue-600 font-medium">Loading...</span>}
        </div> */}

        {dateRangeMode && (
          <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 ">
            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              <CalendarRange className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              <h3 className="text-xs sm:text-sm font-medium text-gray-700">Select Date Range</h3>
            </div>
            <DateSearch onSearch={handleDateRangeSearch} onClear={handleClearSearch} />
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mt-4 sm:mt-6">
        {stats.map((item) => (
          <div
            key={item.id}
            className={`${item.color} rounded-xl sm:rounded-2xl shadow-sm p-3 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between hover:shadow-md transition-all duration-200 active:scale-95`}
          >
            <div className="flex-1 w-full">
              <p className="text-xs sm:text-sm text-gray-600 font-medium">{item.title}</p>
              <h3 className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5 sm:mt-1 truncate">{item.value}</h3>
            </div>
            <div className="p-2 sm:p-3 bg-white rounded-lg sm:rounded-xl shadow-sm mt-2 sm:mt-0 self-end sm:self-auto">{item.icon}</div>
          </div>
        ))}
      </div>

      {/* Chart Section */}
      <div className="mt-6 sm:mt-8">
        <CashFlowChart
          totalIn={totalIn}
          totalOut={totalOut}
          netBalance={netBalance}
          loading={loading}
        />
      </div>
    </div>
  )
}

export default Dashboard
